import React, { createContext, useCallback, useMemo, useState } from 'react';

export const AppAuthContext = createContext({
	isTestAuthEnabled: false,
	testAuthSession: null,
	setTestAuthSession: () => {},
	clearTestAuthSession: () => {}
});

const TEST_AUTH_SESSION_KEY = 'calcium_test_auth_session';

const safeReadTestAuthSession = () => {
	try {
		const raw = localStorage.getItem(TEST_AUTH_SESSION_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object') return null;
		if (!parsed.user || !parsed.token) return null;
		return parsed;
	} catch (_) {
		return null;
	}
};

export const AppAuthProvider = ({ children }) => {
	const isTestAuthEnabled = process.env.REACT_APP_USE_TEST_AUTH === 'true';
	const [testAuthSession, setTestAuthSessionState] = useState(() => (
		isTestAuthEnabled ? safeReadTestAuthSession() : null
	));

	const setTestAuthSession = useCallback((session) => {
		setTestAuthSessionState(session);
		try {
			if (session) {
				localStorage.setItem(TEST_AUTH_SESSION_KEY, JSON.stringify(session));
			} else {
				localStorage.removeItem(TEST_AUTH_SESSION_KEY);
			}
		} catch (_) {}
	}, []);

	const clearTestAuthSession = useCallback(() => {
		setTestAuthSession(null);
	}, [setTestAuthSession]);

	// Never keep a persisted test session active when test auth is disabled.
	React.useEffect(() => {
		if (!isTestAuthEnabled && testAuthSession) {
			setTestAuthSession(null);
		}
	}, [isTestAuthEnabled, testAuthSession, setTestAuthSession]);

	const value = useMemo(() => ({
		isTestAuthEnabled,
		testAuthSession,
		setTestAuthSession,
		clearTestAuthSession
	}), [isTestAuthEnabled, testAuthSession, setTestAuthSession, clearTestAuthSession]);

	return (
		<AppAuthContext.Provider value={value}>
			{children}
		</AppAuthContext.Provider>
	);
};
